import axiosClient from "./axiosClient";
import API_ENDPOINTS from "../constants/apiEndpoints";

/**
 * Register response:
 * { "data": { "status_code": 200, "data": { first_name, ... }, ... } }
 */
function parseRegisterResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const userRow = envelope?.data;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  return { envelope, userRow, statusCode };
}

/**
 * Verify OTP success:
 * { "data": { "status_code": 200, "data": { first_name, ..., "token": "..." }, ... } }
 */
function parseVerifyResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const payload = envelope?.data;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  return { envelope, payload, statusCode };
}

function parseLoginResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  const reason = envelope?.reason ?? envelope?.message ?? "";
  return { envelope, statusCode, reason };
}

function getErrorMessage(err) {
  const d = err?.response?.data;
  const inner = d?.data;
  const innerObj =
    inner && typeof inner === "object" && !Array.isArray(inner) ? inner : null;
  const nested =
    innerObj?.reason ??
    innerObj?.message ??
    (typeof inner === "string" ? inner : null) ??
    d?.reason ??
    d?.message;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  if (Array.isArray(nested)) {
    const joined = nested.filter(Boolean).join(", ").trim();
    if (joined) return joined;
  }
  if (err?.message) return err.message;
  return "Something went wrong. Please try again.";
}

function isVerifyPayloadValid(payload, statusCode) {
  if (statusCode >= 400) return false;
  if (payload == null) return false;
  if (Array.isArray(payload)) return false;
  if (typeof payload !== "object") return false;
  return Boolean(payload.token || payload.access_token);
}

/**
 * Backend shape: `interests[{category}][{index}]` = sub_category name.
 * Example: `interests[legal][0]` → "Estate Planning & Inheritance"
 */
function interestCategoryFormKey(categoryName) {
  return String(categoryName || "")
    .trim()
    .toLowerCase();
}

/** Build POST `/users/create` multipart body from registration fields. */
function buildRegisterFormData(fields = {}) {
  const formData = new FormData();

  const scalars = [
    "email",
    "first_name",
    "last_name",
    "msisdn",
    "location",
    "network",
    "category",
    "sub_category",
    "subscription_consent",
  ];

  scalars.forEach((key) => {
    const value = fields[key];
    if (value == null) return;
    const text = String(value).trim();
    if (text) formData.append(key, text);
  });

  const interests = fields.interests;
  if (interests && typeof interests === "object" && !Array.isArray(interests)) {
    Object.entries(interests).forEach(([category, subs]) => {
      const categoryName = String(category || "").trim();
      if (!categoryName) return;
      const categoryKey = interestCategoryFormKey(categoryName);
      const list = Array.isArray(subs) ? subs : [];
      list.forEach((sub, subIndex) => {
        const subCategoryName = String(sub || "").trim();
        if (!subCategoryName) return;
        formData.append(`interests[${categoryKey}][${subIndex}]`, subCategoryName);
      });
    });
  }

  return formData;
}

/**
 * POST `/users/create`
 * FormData: email, first_name, last_name, msisdn, location, network,
 * category, sub_category, subscription_consent, interests[category][0] (sub_category name), …
 */
export async function registerConsumer(body) {
  const formData = buildRegisterFormData(body);
  const res = await axiosClient.post(API_ENDPOINTS.USER.REGISTER, formData, {
    headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
  });
  const { userRow, statusCode, envelope } = parseRegisterResponse(res);
  if (!userRow || statusCode >= 400) {
    throw new Error(getErrorMessage({ response: { data: envelope } }));
  }
  return userRow;
}

/**
 * POST /user/verify/otp
 * Body: { email, otp }
 */
export async function verifyConsumerOtp(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.VERIFY_OTP, body);
    const { payload, statusCode, envelope } = parseVerifyResponse(res);
    if (!isVerifyPayloadValid(payload, statusCode)) {
      throw new Error(
        getErrorMessage({ response: { data: envelope ?? { data: { reason: "Verification failed" } } } })
      );
    }
    return payload;
  } catch (err) {
    if (err?.response) {
      throw new Error(getErrorMessage(err));
    }
    throw err;
  }
}

/**
 * POST /user/login
 * Body: { email }
 */
export async function requestConsumerLoginOtp(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.LOGIN, body);
    const { envelope, statusCode } = parseLoginResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return envelope;
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

function parseProfileResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const payload = envelope?.data ?? envelope;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  return { envelope, payload, statusCode };
}

/**
 * POST /users/profile/{userId}/update
 * FormData: first_name, last_name, msisdn, location
 */
export async function updateConsumerProfile(userId, fields = {}) {
  const id = userId != null ? String(userId).trim() : "";
  if (!id) {
    throw new Error("User id is required to update profile.");
  }

  const formData = new FormData();
  formData.append("first_name", String(fields.first_name ?? "").trim());
  formData.append("last_name", String(fields.last_name ?? "").trim());
  formData.append("msisdn", String(fields.msisdn ?? "").trim());
  formData.append("location", String(fields.location ?? "").trim());

  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.updateProfile(id), formData, {
      headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
    });
    const { payload, statusCode, envelope } = parseProfileResponse(res);
    if (!payload || statusCode >= 400 || Array.isArray(payload) || typeof payload !== "object") {
      throw new Error(
        getErrorMessage({ response: { data: envelope ?? { data: { reason: "Profile update failed" } } } })
      );
    }
    return payload;
  } catch (err) {
    if (err?.response) {
      throw new Error(getErrorMessage(err));
    }
    throw err;
  }
}

function parseLeadCreateResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  return { envelope, statusCode };
}

function parseLeadListResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  const leads = Array.isArray(envelope?.data) ? envelope.data : [];
  return { envelope, statusCode, leads };
}

function parseReviewCreateResponse(res) {
  const body = res?.data;
  const envelope = body?.data ?? body;
  const statusCode = Number(envelope?.status_code ?? res.status ?? 200);
  const data = envelope?.data;
  return { envelope, statusCode, data };
}

/**
 * POST /user/insurance/application
 * Success envelope may return `data: []` when the lead is created.
 */
export async function submitConsumerInsuranceApplication(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.INSURANCE_APPLICATION, body);
    const { envelope, statusCode } = parseLeadCreateResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return envelope;
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

/**
 * GET /user/leads
 */
export async function listConsumerLeads() {
  try {
    const res = await axiosClient.get(API_ENDPOINTS.USER.LEADS);
    const { envelope, statusCode, leads } = parseLeadListResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return { envelope, leads };
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

/**
 * POST /api/user/create/review
 * Success envelope currently returns `data: []`.
 */
export async function createConsumerReview(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.CREATE_REVIEW, body);
    const { envelope, statusCode, data } = parseReviewCreateResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return { envelope, data };
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

/**
 * POST /auth/forgot-password
 * Body: { email }
 */
export async function requestConsumerForgotPassword(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, body);
    const { envelope, statusCode } = parseLoginResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return envelope;
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

export async function resetConsumerPassword(body) {
  try {
    const res = await axiosClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, body);
    const { envelope, statusCode } = parseLoginResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return envelope;
  } catch (err) {
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

/**
 * POST `/users/add/interest`
 * FormData: user_id, category, sub_category
 * Resolves with `{ user, reason }` where `user` matches sign-in (includes `interests`).
 * 404/405 falls back to `{ _stub: true }` for local flow review.
 */
export async function addConsumerTopicInterest(body = {}) {
  const formData = new FormData();
  const userId = body.user_id ?? body.userId;
  if (userId != null && String(userId).trim() !== "") {
    formData.append("user_id", String(userId).trim());
  }
  const category = String(body.category ?? "").trim();
  const subCategory = String(body.sub_category ?? body.subCategory ?? "").trim();
  if (category) formData.append("category", category);
  if (subCategory) formData.append("sub_category", subCategory);

  try {
    const res = await axiosClient.post(API_ENDPOINTS.USER.ADD_TOPIC_INTEREST, formData, {
      headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
    });
    const { envelope, userRow, statusCode } = parseRegisterResponse(res);
    if (statusCode >= 400) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    if (!userRow || typeof userRow !== "object" || Array.isArray(userRow)) {
      throw new Error(getErrorMessage({ response: { data: envelope } }));
    }
    return {
      user: userRow,
      reason: envelope?.reason ?? envelope?.message ?? "",
    };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 405) {
      // eslint-disable-next-line no-console
      console.warn(
        "[addConsumerTopicInterest] endpoint missing — using stub. Remove when API returns 200.",
      );
      await new Promise((r) => setTimeout(r, 480));
      return { _stub: true };
    }
    if (err?.response) throw new Error(getErrorMessage(err));
    throw err;
  }
}

export { getErrorMessage };
